import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Filter, ArrowUpDown, ArrowUp, ArrowDown, Calendar, Clock, User, ArrowRight, Plus } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import { ArticleCard } from "@/components/blog/ArticleCard";
import { CustomDropdown } from "@/components/blog/CustomDropdown";
import { articles, getCategories, categoryIconsMap, categoryColors, parseDate } from "@/data/blogContent";
import { useIsMobile } from "@/hooks/use-mobile";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema } from "@/components/SchemaOrg";
import bancControleCentre from "@/assets/centre/banc-controle-centre.webp";

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [sortOrder, setSortOrder] = useState<string>("newest");
  const isMobile = useIsMobile();
  const articlesPerPage = isMobile ? 6 : 9;
  const [visibleCount, setVisibleCount] = useState(9); // Default to desktop
  
  // Update visibleCount when isMobile changes (after initial render)
  useEffect(() => {
    setVisibleCount(articlesPerPage);
  }, [isMobile, articlesPerPage]);

  const categories = getCategories();

  // Build category options with counts
  const categoryOptions = useMemo(() => {
    const allOption = { 
      value: "all", 
      label: `Toutes les thématiques (${articles.length})` 
    };
    
    const catOptions = categories.map(cat => {
      const count = articles.filter(a => a.category === cat).length;
      const icon = categoryIconsMap[cat];
      return {
        value: cat,
        label: `${cat} (${count})`,
        icon
      };
    });

    return [allOption, ...catOptions];
  }, [categories]);

  // Sort options
  const sortOptions = [
    { value: "newest", label: "Plus récent", icon: ArrowUp },
    { value: "oldest", label: "Plus ancien", icon: ArrowDown },
  ];

  // Filter and sort articles
  const filteredAndSortedArticles = useMemo(() => {
    let result = activeCategory === "all" 
      ? [...articles] 
      : articles.filter(a => a.category === activeCategory);

    // Sort by date
    result.sort((a, b) => {
      const dateA = parseDate(a.publishedAt);
      const dateB = parseDate(b.publishedAt);
      return sortOrder === "newest" 
        ? dateB.getTime() - dateA.getTime()
        : dateA.getTime() - dateB.getTime();
    });

    return result;
  }, [activeCategory, sortOrder]);

  const featuredArticle = filteredAndSortedArticles[0];
  const gridArticles = filteredAndSortedArticles.slice(1, visibleCount + 1);
  const remainingCount = filteredAndSortedArticles.length - 1 - visibleCount;
  const hasMore = remainingCount > 0;

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + articlesPerPage);
  };

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setVisibleCount(articlesPerPage);
  };

  const handleSortChange = (sort: string) => {
    setSortOrder(sort);
    setVisibleCount(articlesPerPage);
  };

  const CategoryIcon = activeCategory !== "all" ? categoryIconsMap[activeCategory] : null;
  const categoryClass = featuredArticle ? categoryColors[featuredArticle.category] || "bg-gray-100 text-gray-800" : "";

  return (
    <SiteLayout>
      <SEOHead
        title="Blog Contrôle Technique"
        description="Guides et conseils contrôle technique : défauts courants, astuces pour réussir du premier coup, actualités réglementaires 2026. Par les experts AUTOSUR."
        path="/blog"
      />
      <BreadcrumbSchema items={[{ name: "Accueil", href: "/" }, { name: "Blog", href: "/blog" }]} />
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bancControleCentre})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
              ACTUALITÉS
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Blog & Conseils
            </h1>
            <p className="font-body text-white/80 text-lg">
              Retrouvez nos conseils, actualités et informations sur le contrôle technique automobile.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-gray-50 border-b">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <div className="w-full md:w-72">
              <CustomDropdown
                icon={Filter}
                options={categoryOptions}
                value={activeCategory}
                onChange={handleCategoryChange}
                placeholder="Toutes les thématiques"
              />
            </div>
            <div className="w-full md:w-48">
              <CustomDropdown
                icon={ArrowUpDown}
                options={sortOptions}
                value={sortOrder}
                onChange={handleSortChange}
                placeholder="Trier par"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article "À la une" */}
      {featuredArticle && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                {/* Content */}
                <div className="order-2 lg:order-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-heading font-semibold ${categoryClass}`}>
                      {featuredArticle.category}
                    </span>
                    <span className="px-3 py-1 bg-autosur-red text-white rounded-full text-xs font-heading font-semibold">
                      À la une
                    </span>
                  </div>

                  <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-autosur-navy mb-4 leading-tight">
                    {featuredArticle.title}
                  </h2>

                  <p className="font-body text-muted-foreground text-lg mb-6 line-clamp-3">
                    {featuredArticle.excerpt}
                  </p>

                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground font-body mb-6">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {featuredArticle.author}
                    </span>
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {featuredArticle.publishedAt}
                    </span>
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {featuredArticle.readTime}
                    </span>
                  </div>

                  <Link
                    to={`/blog/${featuredArticle.slug}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-autosur-red text-white font-heading font-semibold rounded-full hover:bg-autosur-red-light transition-colors"
                  >
                    Lire l'article
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Visual */}
                <div className="order-1 lg:order-2">
                  <Link to={`/blog/${featuredArticle.slug}`} className="block">
                    <div className="relative aspect-[4/3] rounded-2xl overflow-hidden group">
                      {featuredArticle.image ? (
                        <>
                          <img
                            src={featuredArticle.image}
                            alt={featuredArticle.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-autosur-navy/60 group-hover:bg-autosur-navy/40 transition-colors" />
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-autosur-blue/20 via-autosur-blue/10 to-transparent">
                          {/* Decorative circles with category icon */}
                          <div className="relative">
                            <div className="w-32 h-32 rounded-full bg-autosur-blue/10 absolute -top-4 -left-4" />
                            <div className="w-24 h-24 rounded-full bg-autosur-blue/20 flex items-center justify-center relative z-10">
                              {CategoryIcon && <CategoryIcon className="w-10 h-10 text-autosur-blue" />}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Overlay badge */}
                      <div className="absolute top-4 right-4 px-3 py-1 bg-autosur-red text-white rounded-full text-xs font-heading font-semibold shadow-lg z-10">
                        À la une
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-6">
          {gridArticles.length > 0 ? (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {gridArticles.map((article, index) => (
                  <ArticleCard key={article.slug} article={article} index={index} />
                ))}
              </div>

              {hasMore && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-12 text-center"
                >
                  <button
                    onClick={handleLoadMore}
                    className="group inline-flex items-center gap-2 px-5 py-2.5 border-2 border-muted-foreground/30 text-muted-foreground font-heading font-medium text-sm rounded-full hover:border-autosur-navy hover:text-autosur-navy transition-all whitespace-nowrap"
                  >
                    <Plus className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90" />
                    Voir plus
                    <span className="text-muted-foreground/70 group-hover:text-autosur-navy/70">({remainingCount})</span>
                  </button>
                </motion.div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="font-body text-muted-foreground">
                Aucun article dans cette catégorie pour le moment.
              </p>
            </div>
          )}
        </div>
      </section>

      <CTASection 
        title="Une question sur le contrôle technique ?"
        subtitle="Notre équipe est à votre disposition pour répondre à toutes vos interrogations."
      />
    </SiteLayout>
  );
};

export default BlogPage;
